/** Local archive merge. This is an observed-base precondition, not storage CAS. */
export class ArchiveError extends Error {
  constructor(code: 'SG-E507' | 'SG-E508' | 'SG-E509' | 'SG-E510' | 'SG-E511' | 'SG-E512', readonly recovery?: { original: unknown; draft: unknown }) { super(code); }
}
export const sameArchiveValue = (a: unknown, b: unknown): boolean => stable(a) === stable(b);
function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.entries(value).filter(([, v]) => v !== undefined).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${JSON.stringify(k)}:${stable(v)}`).join(',')}}`;
  return JSON.stringify(value) ?? 'undefined';
}
const object = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
const keyed = (v: unknown[]): v is { id: string }[] => v.every(x => object(x) && typeof x.id === 'string');
function uniqueIds(items: { id: string }[]) { if (new Set(items.map(x => x.id)).size !== items.length) throw new ArchiveError('SG-E508'); }
export function mergeObserved<T>(base: T | undefined, local: T | undefined, current: T | undefined): T | undefined {
  if (sameArchiveValue(local, current)) return current;
  if (sameArchiveValue(local, base)) return current;
  if (sameArchiveValue(current, base)) return local;
  if (Array.isArray(base) && Array.isArray(local) && Array.isArray(current) && keyed(base) && keyed(local) && keyed(current)) {
    [base, local, current].forEach(uniqueIds);
    const ids = [...new Set([...current, ...local, ...base].map(x => x.id))];
    return ids.map(id => mergeObserved(base.find(x => x.id === id), local.find(x => x.id === id), current.find(x => x.id === id))).filter(x => x !== undefined) as T;
  }
  if (object(base) && object(local) && object(current)) {
    const out: Record<string, unknown> = {};
    for (const key of new Set([...Object.keys(base), ...Object.keys(local), ...Object.keys(current)])) {
      const value = mergeObserved(base[key], local[key], current[key]);
      if (value !== undefined) out[key] = value;
    }
    return out as T;
  }
  throw new ArchiveError('SG-E511');
}
export function mergeEntity<T extends { id: string }>(base: T | null, local: T, current: T | undefined): T {
  if (base === null) {
    if (current && !sameArchiveValue(local, current)) throw new ArchiveError('SG-E511');
    return current ?? local;
  }
  if (base.id !== local.id || !current) throw new ArchiveError('SG-E511');
  return mergeObserved(base, local, current)!;
}
